import { useInfiniteQuery, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  addDays,
  ANALYTICS_AREAS,
  ANALYTICS_PERIOD_LIMIT_DAYS,
  eatDayOf,
  formatIsoDay,
  isAnalyticsArea,
  type AnalyticsArea,
} from '@wodi/domain';
import {
  ANALYTICS_ACCESS,
  type AnalyticsColumn,
  type AnalyticsQuery,
  type AnalyticsTable,
  type AnalyticsView,
  type ReportPeriod,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  TrendChart,
} from '@wodi/ui';
import { useState } from 'react';

import {
  analyticsKeys,
  analyticsSetupQuery,
  downloadAnalytics,
  fetchAnalytics,
  fetchDrill,
} from '../api/analytics.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText, LoadFailed } from '../khis/format.tsx';
import { screen, TableScroll } from '../layout/screen.tsx';
import {
  AsAt,
  BackToReports,
  PeriodFields,
  periodIsValid,
  periodWords,
} from '../reports/report-frame.tsx';
import { toasts } from '../toasts.ts';
import styles from './analytics.module.css';
import { cellWords, chartValue, isEmptyView, isNumeric, summarySentence } from './words.ts';

// Reports > Analytics (docs/plans/phase3-deeper-analytics.md §5): one area per page, the same
// filter bar and layout for each. A written summary, a chart per trend with its table view, a
// table per breakdown, and the rows behind a figure on request. Filters stay in component
// state, never the address. Not a grid of KPI cards (DESIGN.md §8, §11).

const ALL = 'all';
const DEFAULT_DAYS = 30;

const GRAIN_WORDS: Record<AnalyticsView['grain'], string> = {
  day: 'Day',
  week: 'Week from',
  month: 'Month',
};

function defaultPeriod(now: Date = new Date()): ReportPeriod {
  const today = eatDayOf(now);
  return { from: formatIsoDay(addDays(today, 1 - DEFAULT_DAYS)), to: formatIsoDay(today) };
}

function AreaTabs({ areas }: { areas: readonly AnalyticsArea[] }) {
  return (
    <nav aria-label="Analytics" className={screen.tabs}>
      {areas.map((a) => (
        <Link key={a} to="/reports/analytics/$area" params={{ area: a }}>
          {ANALYTICS_ACCESS[a].title}
        </Link>
      ))}
    </nav>
  );
}

function CellText({ column, value }: { column: AnalyticsColumn; value: string | null }) {
  const words = cellWords(column.unit, value);
  return column.unit === 'id' && value !== null ? (
    <span className={styles.id}>{words}</span>
  ) : (
    words
  );
}

function FigureTable({
  label,
  columns,
  rows,
  action,
}: {
  label: string;
  columns: readonly AnalyticsColumn[];
  rows: AnalyticsTable['rows'];
  action?: { label: string; onOpen: (row: AnalyticsTable['rows'][number]) => void } | undefined;
}) {
  return (
    <TableScroll>
      <Table flat aria-label={label}>
        <TableHeader>
          {[
            ...columns.map((c, i) => (
              <Column key={c.key} isRowHeader={i === 0} numeric={isNumeric(c.unit)}>
                {c.label}
              </Column>
            )),
            ...(action === undefined
              ? []
              : [
                  <Column key="action">
                    <span className={styles.visuallyHidden}>Open</span>
                  </Column>,
                ]),
          ]}
        </TableHeader>
        <TableBody items={rows.map((r) => ({ ...r, id: r.key }))}>
          {(row) => (
            <Row id={row.id}>
              {[
                ...columns.map((c, i) => (
                  <Cell key={c.key} numeric={isNumeric(c.unit)}>
                    <CellText column={c} value={row.cells[i] ?? null} />
                  </Cell>
                )),
                ...(action === undefined
                  ? []
                  : [
                      <Cell key="action">
                        <Button
                          dense
                          variant="quiet"
                          aria-label={`${action.label}: ${row.cells[0] ?? ''}`}
                          onPress={() => {
                            action.onOpen(row);
                          }}
                        >
                          {action.label}
                        </Button>
                      </Cell>,
                    ]),
              ]}
            </Row>
          )}
        </TableBody>
      </Table>
    </TableScroll>
  );
}

function Drill({
  area,
  query,
  table,
  row,
  onClose,
}: {
  area: AnalyticsArea;
  query: AnalyticsQuery;
  table: AnalyticsTable;
  row: AnalyticsTable['rows'][number];
  onClose: () => void;
}) {
  const online = useOnline();
  const pages = useInfiniteQuery({
    queryKey: analyticsKeys.drill(area, query, table.key, row.key),
    queryFn: ({ pageParam }) =>
      fetchDrill(area, { ...query, table: table.key, rowKey: row.key, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const first = pages.data?.pages[0];
  const rows = pages.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <Section
      title={first === undefined ? 'Loading the list' : `${first.title}: ${row.cells[0] ?? ''}`}
      headingLevel={3}
      actions={
        <Button dense variant="quiet" onPress={onClose}>
          Close list
        </Button>
      }
    >
      {pages.isPending ? <LoadingBlock label="Loading the list" rows={3} /> : null}
      {pages.isError ? (
        <LoadFailed error={pages.error} what="The list" retry={() => void pages.refetch()} />
      ) : null}
      {first === undefined ? null : rows.length === 0 ? (
        <EmptyState>Nothing to list for this row.</EmptyState>
      ) : (
        <>
          <FigureTable label={first.title} columns={first.columns} rows={rows} />
          {pages.hasNextPage ? (
            <div>
              <Button
                onPress={() => void pages.fetchNextPage()}
                isDisabled={!online}
                isPending={pages.isFetchingNextPage}
                pendingLabel="Loading"
              >
                Show more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Section>
  );
}

function Breakdown({
  area,
  query,
  table,
  mayDrill,
}: {
  area: AnalyticsArea;
  query: AnalyticsQuery;
  table: AnalyticsTable;
  mayDrill: boolean;
}) {
  const [open, setOpen] = useState<AnalyticsTable['rows'][number] | null>(null);
  const action =
    mayDrill && table.drillLabel !== null
      ? { label: table.drillLabel, onOpen: setOpen }
      : undefined;
  return (
    <Section
      title={table.title}
      {...(table.description === null ? {} : { description: table.description })}
    >
      {table.rows.length === 0 ? (
        <EmptyState>Nothing recorded for this in the period.</EmptyState>
      ) : (
        <FigureTable
          label={table.title}
          columns={table.columns}
          rows={table.rows}
          action={action}
        />
      )}
      {table.truncated ? (
        <p className={styles.meta}>
          Only the first rows are shown. Choose a department or a shorter period to see others.
        </p>
      ) : null}
      {open === null ? null : (
        <Drill
          key={open.key}
          area={area}
          query={query}
          table={table}
          row={open}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </Section>
  );
}

function Results({
  area,
  query,
  view,
  mayDrill,
  mayExport,
}: {
  area: AnalyticsArea;
  query: AnalyticsQuery;
  view: AnalyticsView;
  mayDrill: boolean;
  mayExport: boolean;
}) {
  const online = useOnline();
  const download = useMutation({
    mutationFn: () => downloadAnalytics(area, query),
    onSuccess: () => {
      toasts.show({ title: 'Download started' });
    },
  });
  return (
    <>
      <div className={styles.resultHead}>
        <AsAt dataAsAt={view.dataAsAt} />
        {mayExport ? (
          <Button
            onPress={() => {
              download.mutate();
            }}
            isDisabled={!online}
            isPending={download.isPending}
            pendingLabel="Downloading"
          >
            Download CSV
          </Button>
        ) : null}
      </div>
      {download.isError ? (
        <Notice tone="error">
          {errorText(download.error, "The file wasn't downloaded. Try again.")}
        </Notice>
      ) : null}
      <p className={styles.summary}>
        {`${view.facility.name}, ${periodWords(view.period)}. ${summarySentence(view)}`}
      </p>
      {view.labelsLimited ? (
        <Notice tone="info">
          Visits with sensitivity labels your roles don&apos;t hold are not counted here.
        </Notice>
      ) : null}
      {isEmptyView(view) ? (
        <EmptyState>Nothing was recorded for this facility in this period.</EmptyState>
      ) : (
        <>
          {view.series.length === 0 ? null : (
            <Section title="Over the period">
              <div className={styles.charts}>
                {view.series.map((s) => (
                  <TrendChart
                    key={s.key}
                    title={`${s.title}, ${periodWords(view.period)}`}
                    valueLabel={s.title}
                    periodLabel={GRAIN_WORDS[view.grain]}
                    points={view.buckets.map((b, i) => ({
                      label: b.label,
                      value: chartValue(s.unit, s.points[i] ?? null),
                      valueText: cellWords(s.unit, s.points[i] ?? null),
                    }))}
                  />
                ))}
              </div>
            </Section>
          )}
          {view.tables.map((t) => (
            <Breakdown key={t.key} area={area} query={query} table={t} mayDrill={mayDrill} />
          ))}
        </>
      )}
    </>
  );
}

function AreaScreen({ area }: { area: AnalyticsArea }) {
  const setup = useQuery(analyticsSetupQuery);
  const online = useOnline();
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [period, setPeriod] = useState<ReportPeriod>(defaultPeriod);
  const [department, setDepartment] = useState(ALL);
  const facilities = (setup.data?.facilities ?? []).filter((f) =>
    f.areas.some((a) => a.area === area),
  );
  const facility =
    facilities.find((f) => f.facilityId === facilityId) ??
    facilities.find((f) => f.current) ??
    facilities[0];
  const access = facility?.areas.find((a) => a.area === area);
  const departments = area === 'claims' || facility === undefined ? [] : facility.departments[area];
  const queryOf = (): AnalyticsQuery => ({
    period,
    ...(facility === undefined || facility.current ? {} : { facilityId: facility.facilityId }),
    ...(department === ALL ? {} : { department }),
  });
  const [applied, setApplied] = useState<AnalyticsQuery>(() => ({ period }));
  const view = useQuery({
    queryKey: analyticsKeys.view(area, applied),
    queryFn: () => fetchAnalytics(area, applied),
    enabled: setup.data !== undefined && facility !== undefined,
  });
  const valid = periodIsValid(period, ANALYTICS_PERIOD_LIMIT_DAYS);

  if (setup.isPending) return <LoadingBlock label="Loading the analytics" rows={4} />;
  if (setup.isError)
    return (
      <LoadFailed error={setup.error} what="The analytics" retry={() => void setup.refetch()} />
    );
  if (facility === undefined)
    return (
      <Notice tone="info">
        {`You don't have permission to see ${ANALYTICS_ACCESS[area].title.toLowerCase()} at any facility. Ask an administrator if you need it.`}
      </Notice>
    );
  return (
    <>
      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) setApplied(queryOf());
        }}
      >
        {facilities.length > 1 ? (
          <Select
            label="Facility"
            options={facilities.map((f) => ({ id: f.facilityId, label: f.name }))}
            value={facility.facilityId}
            onChange={(v) => {
              setFacilityId(String(v));
              setDepartment(ALL);
            }}
          />
        ) : null}
        <PeriodFields period={period} onChange={setPeriod} maxDays={ANALYTICS_PERIOD_LIMIT_DAYS} />
        {departments.length === 0 ? null : (
          <Select
            label="Department"
            options={[{ id: ALL, label: 'All departments' }, ...departments]}
            value={department}
            onChange={(v) => {
              setDepartment(String(v));
            }}
          />
        )}
        <div>
          <Button type="submit" variant="primary" isDisabled={!valid || !online}>
            Show
          </Button>
        </div>
      </form>
      {online ? null : (
        <Notice tone="info">
          {view.data === undefined
            ? "You're offline. Analytics need a connection."
            : "You're offline. Analytics need a connection. What's shown may be out of date."}
        </Notice>
      )}
      {view.isPending && view.fetchStatus !== 'idle' ? (
        <LoadingBlock label="Loading the figures" rows={6} />
      ) : null}
      {view.isError && online ? (
        <LoadFailed error={view.error} what="The figures" retry={() => void view.refetch()} />
      ) : null}
      {view.data === undefined ? null : (
        <Results
          area={area}
          query={applied}
          view={view.data}
          mayDrill={access?.drill ?? false}
          mayExport={access?.export ?? false}
        />
      )}
    </>
  );
}

export function AnalyticsPage({ area }: { area: string }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const readable = ANALYTICS_AREAS.filter(
    (a) => session !== null && ANALYTICS_ACCESS[a].read.every((p) => can(session, p)),
  );
  if (!isAnalyticsArea(area))
    return (
      <div className={styles.page}>
        <BackToReports />
        <PageHeader title="Report not found" />
        <EmptyState>There is no analytics page at this address.</EmptyState>
      </div>
    );
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader
        title={ANALYTICS_ACCESS[area].title}
        description={ANALYTICS_ACCESS[area].description}
      />
      {readable.length > 1 ? <AreaTabs areas={readable} /> : null}
      {readable.includes(area) ? (
        <AreaScreen key={area} area={area} />
      ) : (
        <Notice tone="info">
          {`You don't have permission to see ${ANALYTICS_ACCESS[area].title.toLowerCase()}. Ask an administrator if you need it.`}
        </Notice>
      )}
    </div>
  );
}
